// GENERATED from vec-core-backend/bin/orderTotals.js - edit that file and run `node bin/syncAdminUtils.js` (a backend test fails when they differ).
/**
 * Order totals - the one place the order-level money arithmetic lives.
 *
 * Why it exists: the seal, the draft update, the delivery resolve, the order screen, the prints and the reports each
 * re-did this arithmetic with float maths and their own rounding, so the same order could total to different cents in
 * different places (e.g. a "target price" order sealing 1 cent over what the cashier saw). Everything here is done in
 * integer cents and every part is rounded exactly once, so the parts always add up to the grand total.
 *
 * A copy of this file lives in vec-core-admin (src/utils/orderTotals.js). Keep them identical; both are tested against
 * the same fixtures (see __tests__/orderTotals.test.js).
 *
 * Line level maths is NOT here: each line already carries its own lineTotal / lineVat, computed when the line is saved.
 * This module combines those into the order totals.
 *
 * Orders sealed before the "VAT after discount" start date (DO_NOT_CHANGE__VAT_DISCOUNT_ALTERATION_DATE) used a different
 * order of operations and are never recalculated: readers use what was stored (see storedTotals / legacyTotals below).
 */

const ALGORITHM_VERSION = 2;

// Round half away from zero to whole cents. toPrecision(15) strips float noise first (1.005 * 100 = 100.49999999999999 must
// still round to 101) without the "round to 3 decimals, then to 2" double rounding that turned 907.8947 into 907.90.
function toCents(v) {
    if (v === null || v === undefined || v === '') return 0;
    const n = typeof v === 'number' ? v : parseFloat(v);
    if (!Number.isFinite(n)) throw new Error(`Cannot total a non-numeric amount (${v}).`);
    const x = parseFloat((Math.abs(n) * 100).toPrecision(15));
    return (Math.sign(n) * Math.round(x)) || 0;
}
const fromCents = (c) => c / 100;

const r2 = (v) => fromCents(toCents(v));

/**
 * One order line: discount, unit price after discount, line total and the VAT in / on it. This is THE line formula - the seal,
 * the draft update, the delivery resolve, line add/edit and the order screen all use it, so a line is worth the same cents
 * wherever it is looked at.
 *
 * @param {object} l
 * @param {number} l.quantity        already rounded to the system quantity precision
 * @param {number} l.unitPrice
 * @param {number} l.discountType    1 = fixed amount per unit, anything else = percentage
 * @param {number} l.discountValue
 * @param {number} l.taxType         1 zero rated, 2 VAT inclusive (price contains VAT), 3 VAT exclusive (VAT added on top)
 * @param {number} l.vatRate         e.g. 0.125
 * @returns {{discountVal:number,newUnit:number,lineTotal:number,vat:number,zeroRated:number,standard:number}}
 */
function lineAmounts(l) {
    const unit = r2(l.unitPrice);
    const discountVal = parseInt(l.discountType) === 1 ? r2(l.discountValue || 0) : r2(parseFloat(l.unitPrice) * ((parseFloat(l.discountValue) || 0) / 100));
    const newUnit = r2(unit - discountVal);
    // Unit conversion lines can bill a rounded-up quantity while inventory keeps the exact one.
    const billQty = l.billableQuantity !== undefined && l.billableQuantity !== null ? l.billableQuantity : l.quantity;
    const lineTotal = r2((parseFloat(billQty) || 0) * newUnit);
    const rate = parseFloat(l.vatRate) || 0;
    const type = parseInt(l.taxType);
    let vat = 0, zeroRated = 0, standard = 0;
    if (type === 2 && rate > 0) { vat = r2(lineTotal / ((1 + rate) / rate)); standard = r2(lineTotal - vat); }
    else if (type === 3) { vat = r2(lineTotal * rate); standard = lineTotal; }
    else if (type === 2) { standard = lineTotal; }
    else zeroRated = lineTotal;
    return { discountVal, newUnit, lineTotal, vat, zeroRated, standard };
}

/**
 * @param {object} p
 * @param {number} p.zeroRated        sum of zero rated line totals
 * @param {number} p.standard         sum of standard rated lines, net of VAT
 * @param {number} p.vatItems         sum of the lines' VAT
 * @param {number} p.discountAmount   whole order discount (net of VAT)
 * @param {number} p.deliveryCost
 * @param {number} [p.ccRatePct]      card charge percentage (0 / undefined = none)
 * @param {number} [p.vatAdjustCents] +/- cents the target price solver moved VAT by (see solveTargetDiscount)
 */
function orderTotals(p) {
    const zero = toCents(p.zeroRated);
    const std = toCents(p.standard);
    const vatItems = toCents(p.vatItems);
    const base = zero + std;
    let disc = Math.max(0, toCents(p.discountAmount));
    if (disc > base) disc = base; // a discount can never take the order below 0
    const delivery = toCents(p.deliveryCost);

    // the discount comes off the net amounts; split it across zero rated / standard so the two always add back up
    const zeroShare = base ? Math.round((disc * zero) / base) : 0;
    const stdShare = disc - zeroShare;
    const zeroAfter = zero - zeroShare;
    const stdAfter = std - stdShare;
    // VAT follows the standard rated net amount down
    let vatAfter = base ? Math.round((vatItems * (base - disc)) / base) : vatItems;
    const adjust = parseInt(p.vatAdjustCents) || 0;
    if (adjust) vatAfter = Math.max(0, vatAfter + adjust);

    const subtotal = zeroAfter + stdAfter + vatAfter + delivery;
    const rate = parseFloat(p.ccRatePct) || 0;
    const cc = rate > 0 ? toCents(fromCents(subtotal) * (rate / 100)) : 0;

    return {
        zeroRated: fromCents(zero),
        standard: fromCents(std),
        discount: fromCents(disc),
        zeroRatedAfterDiscount: fromCents(zeroAfter),
        standardAfterDiscount: fromCents(stdAfter),
        vatAfterDiscount: fromCents(vatAfter),
        subtotal: fromCents(subtotal),
        deliveryCost: fromCents(delivery),
        additionalCharges: fromCents(cc),
        grandTotal: fromCents(subtotal + cc),
        vatAdjustCents: adjust,
        algorithmVersion: ALGORITHM_VERSION
    };
}

/**
 * orderTotals for a stored order: when the order carries a target price (metadata.targetGrandTotal, set by the target price
 * dialog) and the stored discount is still the one that target produces for the CURRENT lines, the one-cent VAT nudge is applied;
 * if the lines changed since, the target is stale and ignored.
 */
function computeForOrder(p) {
    let adjust = parseInt(p.vatAdjustCents) || 0;
    const target = parseFloat(p.targetGrandTotal);
    if (Number.isFinite(target) && target > 0) {
        const s = solveTargetDiscount({ ...p, vatAdjustCents: 0 }, target);
        adjust = toCents(s.discountAmount) === toCents(p.discountAmount) ? s.vatAdjustCents : 0;
    }
    return orderTotals({ ...p, vatAdjustCents: adjust });
}

/**
 * "Target price": find the whole order discount that makes the order total exactly `targetTotal`.
 *
 * The discount is a whole number of cents. Because VAT follows the discount down, one cent of discount moves the total by a
 * little more than one cent, so some targets fall between two reachable totals. In that case the closest discount is used and
 * VAT is nudged by the missing cent (never more than 1) so the total is exactly what was asked for; the nudge is recorded
 * in totals.vatAdjustCents and must be passed back to orderTotals when the order is recalculated.
 *
 * @returns {{discountAmount:number, vatAdjustCents:number, totals:object, exact:boolean}}
 */
function solveTargetDiscount(p, targetTotal) {
    const target = toCents(targetTotal);
    const base = toCents(p.zeroRated) + toCents(p.standard);
    const noDisc = orderTotals({ ...p, discountAmount: 0, vatAdjustCents: 0 });
    const gross = toCents(noDisc.grandTotal);
    if (target >= gross) return { discountAmount: 0, vatAdjustCents: 0, totals: noDisc, exact: target === gross };

    // Newton style search on whole cents: one cent of discount moves the total by `slope` cents (VAT and card charge follow it down)
    const slope = (1 + toCents(p.vatItems) / Math.max(1, base)) * (1 + (parseFloat(p.ccRatePct) || 0) / 100);
    let d = Math.min(base, Math.max(0, Math.round((gross - target) / slope)));
    for (let i = 0; i < 12; i++) {
        const miss = toCents(orderTotals({ ...p, discountAmount: fromCents(d), vatAdjustCents: 0 }).grandTotal) - target;
        if (Math.abs(miss) <= 1) break;
        const next = Math.min(base, Math.max(0, d + Math.round(miss / slope)));
        if (next === d) break;
        d = next;
    }
    // then pick the best whole-cent discount around it, allowing VAT to move by one cent: exact hit first, then the
    // smallest VAT nudge, then the smaller discount
    let best = null;
    for (let c = Math.max(0, d - 3); c <= Math.min(base, d + 3); c++) {
        for (const adj of [0, 1, -1]) {
            const t = orderTotals({ ...p, discountAmount: fromCents(c), vatAdjustCents: adj });
            const miss = target - toCents(t.grandTotal);
            const better = !best || Math.abs(miss) < Math.abs(best.miss)
                || (Math.abs(miss) === Math.abs(best.miss) && (Math.abs(adj) < Math.abs(best.adj) || (Math.abs(adj) === Math.abs(best.adj) && c < best.d)));
            if (better) best = { d: c, adj, miss, t };
        }
    }
    const adjust = best.adj;
    const totals = best.t;
    return { discountAmount: fromCents(best.d), vatAdjustCents: adjust, totals, exact: toCents(totals.grandTotal) === target };
}

/**
 * Totals for an order sealed BEFORE the VAT-after-discount start date and carrying no metadata.totals (derived for display only,
 * never stored). In that era the whole order discount came off the VAT-inclusive total and VAT was not reduced by it:
 *   grand = lines + VAT on exclusive lines - discount + delivery (+ card charge on that subtotal)
 * Lines are { lineTotal, lineVat, taxType } as stored on the order line (metadata.lineTotal, vatData.lineVatTotal, vatData.taxType).
 */
function legacyTotals(p) {
    let zero = 0, std = 0, vat = 0;
    for (const l of p.lines || []) {
        const lt = toCents(l.lineTotal);
        const lv = toCents(l.lineVat);
        const type = parseInt(l.taxType);
        vat += lv;
        if (type === 1) zero += lt;
        else if (type === 2) std += lt - lv; // price already includes the VAT
        else std += lt; // exclusive, VAT is added on top
    }
    const exclusiveVat = (p.lines || []).filter((l) => parseInt(l.taxType) === 3).reduce((a, l) => a + toCents(l.lineVat), 0);
    // a discount never takes the order below zero (some old orders carry a discount on zero priced lines)
    const disc = Math.min(Math.max(0, toCents(p.discountAmount)), zero + std + vat);
    const delivery = toCents(p.deliveryCost);
    const subtotal = zero + std + vat - disc + delivery;
    const rate = parseFloat(p.ccRatePct) || 0;
    const cc = rate > 0 ? toCents(fromCents(subtotal) * (rate / 100)) : 0;
    return {
        zeroRated: fromCents(zero), standard: fromCents(std), discount: fromCents(disc),
        zeroRatedAfterDiscount: fromCents(zero), standardAfterDiscount: fromCents(std - disc), vatAfterDiscount: fromCents(vat),
        subtotal: fromCents(subtotal), deliveryCost: fromCents(delivery), additionalCharges: fromCents(cc), grandTotal: fromCents(subtotal + cc),
        exclusiveVat: fromCents(exclusiveVat), algorithmVersion: 0, derived: true
    };
}

/**
 * legacyTotals for an order read straight from its stored line items (quantity, unitPrice, discount, vatData). Line totals are
 * recalculated with the shared line formula rather than read from metadata.lineTotal, because that field changed meaning over time
 * (VAT inclusive in old orders, net of VAT in newer ones). The VAT of each line is the one stored when the order was sealed.
 */
function deriveFromLines(lines, p) {
    const out = (lines || []).map((l) => {
        const vd = l.vatData || {};
        const la = lineAmounts({ quantity: l.quantity, billableQuantity: l.metadata && l.metadata.billableQuantity, unitPrice: l.unitPrice, discountType: l.discountType, discountValue: l.discountValue, taxType: vd.taxType, vatRate: vd.value !== undefined ? vd.value : p.vatRate });
        return { lineTotal: la.lineTotal, lineVat: vd.lineVatTotal !== undefined && vd.lineVatTotal !== null ? vd.lineVatTotal : la.vat, taxType: vd.taxType };
    });
    return legacyTotals({ lines: out, discountAmount: p.discountAmount, deliveryCost: p.deliveryCost, ccRatePct: p.ccRatePct });
}

/**
 * What an order's stored totals say, for readers (prints, reports, screens). Orders sealed with this algorithm or the
 * earlier one carry metadata.totals; very old orders do not, and for those only the numbers that were really stored are
 * returned (null for the rest) - nothing is recalculated, because the old order of operations cannot be reproduced.
 */
function storedTotals(order) {
    const md = (order && order.metadata) || {};
    const t = md.totals || null;
    const gt = md.grandTotal !== undefined && md.grandTotal !== null && md.grandTotal !== '' ? parseFloat(md.grandTotal) : null;
    return {
        hasBreakdown: !!t,
        grandTotal: t && t.grandTotal !== undefined ? parseFloat(t.grandTotal) : gt,
        zeroRated: t ? num(t.zeroRated) : null,
        standard: t ? num(t.standard) : null,
        zeroRatedAfterDiscount: t ? num(t.zeroRatedAfterDiscount !== undefined ? t.zeroRatedAfterDiscount : t.zeroRatedAfterDiscounts) : null,
        standardAfterDiscount: t ? num(t.standardAfterDiscount) : null,
        vatAfterDiscount: t ? num(t.vatAfterDiscount) : null,
        deliveryCost: t ? num(t.deliveryCost) : null,
        additionalCharges: t ? num(t.additionalCharges) : null,
        discount: t ? num(t.discount) : null,
        algorithmVersion: t && t.algorithmVersion ? t.algorithmVersion : (t ? 1 : 0)
    };
}
function num(v) {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
}

/**
 * The numbers an order's print or screen should show. Always the stored ones when the order has them (so a print can never
 * disagree with the invoice); orders with no stored breakdown are rebuilt from their line items for display only, and keep their
 * stored grand total. Call it BEFORE the print code formats the line items into currency strings.
 */
function displayTotals(order, opts = {}) {
    const o = order || {};
    const st = storedTotals(o);
    const md = o.metadata || {};
    const lines = o.OrderLineItems || [];
    const delivery = num(o.deliveryInfo && o.deliveryInfo.cost);
    if (st.hasBreakdown && st.grandTotal !== null) {
        return {
            derived: false, zeroRated: st.zeroRated, standard: st.standard, subtotal: fromCents(toCents(st.zeroRated) + toCents(st.standard)),
            zeroRatedAfterDiscount: st.zeroRatedAfterDiscount, standardAfterDiscount: st.standardAfterDiscount, vatAfterDiscount: st.vatAfterDiscount,
            discount: st.discount !== null ? st.discount : num(o.discountAmount), deliveryCost: st.deliveryCost !== null ? st.deliveryCost : delivery,
            additionalCharges: st.additionalCharges !== null ? st.additionalCharges : num(md.ccCharges), grandTotal: st.grandTotal
        };
    }
    if (lines.length) {
        const d = deriveFromLines(lines, {
            discountAmount: o.discountAmount, deliveryCost: delivery, ccRatePct: md.addCreditCardCharges ? md.creditCardChargesRate : 0, vatRate: opts.vatRate
        });
        return {
            derived: true, zeroRated: d.zeroRated, standard: d.standard, subtotal: fromCents(toCents(d.zeroRated) + toCents(d.standard)),
            zeroRatedAfterDiscount: d.zeroRatedAfterDiscount, standardAfterDiscount: d.standardAfterDiscount, vatAfterDiscount: d.vatAfterDiscount,
            discount: d.discount, deliveryCost: d.deliveryCost, additionalCharges: d.additionalCharges, grandTotal: st.grandTotal !== null ? st.grandTotal : d.grandTotal
        };
    }
    return {
        derived: true, zeroRated: 0, standard: 0, subtotal: 0, zeroRatedAfterDiscount: 0, standardAfterDiscount: 0, vatAfterDiscount: 0,
        discount: num(o.discountAmount), deliveryCost: delivery, additionalCharges: num(md.ccCharges), grandTotal: st.grandTotal !== null ? st.grandTotal : 0
    };
}

export { ALGORITHM_VERSION, toCents, fromCents, lineAmounts, computeForOrder, orderTotals, solveTargetDiscount, legacyTotals, deriveFromLines, storedTotals, displayTotals };
